'use client';

import { c as _c } from "react/compiler-runtime";
import { FollowersListModalType } from '@shared/all/domains/profile';
import { type Result } from '@shared/all/domains/request';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useUser from '@shared/utils/hooks/useUser';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { type RouterOutputs } from '../../utils/trpc';
import { useAuthModals } from '../../widgets/WidgetsContext';
import InlineProfileCard, { type ListUser } from './InlineProfileCard';
type Props = {
  title?: string;
  username?: string;
  isSpace?: boolean;
  fetchFunction: (cursor: number | undefined) => Promise<Result<{
    users: ListUser[];
    count?: number;
  }>>;
  type?: FollowersListModalType;
  space?: RouterOutputs['spaces']['get'];
};
const UsersList = props => {
  const $ = _c(50);
  const {
    currentUser
  } = useUser();
  const {
    openAuthContinueModal
  } = useAuthModals();
  const {
    ref: loadingSectionRef,
    inView
  } = useInView();
  let t0;
  if ($[0] !== props.title || $[1] !== props.type || $[2] !== props.username) {
    t0 = ["UsersList", props.username, props.type, props.title];
    $[0] = props.title;
    $[1] = props.type;
    $[2] = props.username;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== props) {
    t1 = t2 => {
      const {
        pageParam
      } = t2;
      return unwrapFromResult(props.fetchFunction(pageParam));
    };
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      initialPageParam: undefined as number | undefined,
      getNextPageParam: _temp,
      select: _temp3
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    data: userList,
    isLoading: loading,
    error,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery(t2);
  let t3;
  if ($[9] !== fetchNextPage || $[10] !== inView || $[11] !== loading) {
    t3 = () => {
      if (inView && !loading) {
        fetchNextPage();
      }
    };
    $[9] = fetchNextPage;
    $[10] = inView;
    $[11] = loading;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== inView) {
    t4 = [inView];
    $[13] = inView;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[15] !== error) {
    t5 = () => {
      if (error) {
        toast(error?.message || String(error), "", "error");
      }
    };
    t6 = [error];
    $[15] = error;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  if ($[18] !== currentUser || $[19] !== error || $[20] !== loading || $[21] !== openAuthContinueModal || $[22] !== props.isSpace || $[23] !== props.space || $[24] !== props.title || $[25] !== userList) {
    t7 = !loading && !error && userList && userList?.length > 0 && userList?.map(aUser => <InlineProfileCard key={aUser.id} showAuthModal={openAuthContinueModal} loggedIn={currentUser.isAuthenticated} title={props.title} isSpace={props.isSpace} user={aUser} space={props.space} />);
    $[18] = currentUser;
    $[19] = error;
    $[20] = loading;
    $[21] = openAuthContinueModal;
    $[22] = props.isSpace;
    $[23] = props.space;
    $[24] = props.title;
    $[25] = userList;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t7) {
    t8 = <div className="space-y-1">{t7}</div>;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== loading) {
    t9 = loading && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-textSecondary/70" size={24} /></div>;
    $[29] = loading;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== error) {
    t10 = error && <p className="text-center text-destructive">An error occurred. Please try again later.</p>;
    $[31] = error;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== error || $[34] !== loading || $[35] !== props.title || $[36] !== props.type || $[37] !== props.username || $[38] !== userList?.length) {
    t11 = !loading && !error && userList?.length === 0 && (props.type === FollowersListModalType.FOLLOWING ? <p className="text-center text-textSecondary/70">@{props.username} isn't following anyone yet.</p> : <p className="text-center text-textSecondary/70">{props.title === "Muted Users" ? "You haven't muted anyone." : props.title === "Blocked Users" ? "You haven't blocked anyone." : "No users to show."}</p>);
    $[33] = error;
    $[34] = loading;
    $[35] = props.title;
    $[36] = props.type;
    $[37] = props.username;
    $[38] = userList?.length;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const t12 = `flex w-full justify-center ${hasNextPage ? "block" : "hidden"}`;
  let t13;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Loader2 className="animate-spin" height={50} width={50} />;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== loadingSectionRef || $[42] !== t12) {
    t14 = <div ref={loadingSectionRef} className={t12}>{t13}</div>;
    $[41] = loadingSectionRef;
    $[42] = t12;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t10 || $[45] !== t11 || $[46] !== t14 || $[47] !== t8 || $[48] !== t9) {
    t15 = <>{t8}{t9}{t10}{t11}{t14}</>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t14;
    $[47] = t8;
    $[48] = t9;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
};
export default UsersList;
function _temp(lastPage) {
  if (lastPage.users.length < 10) {
    return;
  }
  return lastPage.users[lastPage.users.length - 1]?.id;
}
function _temp2(page) {
  return page.users;
}
function _temp3(data) {
  return data.pages.flatMap(_temp2);
}